import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterOutlet, NavigationEnd } from '@angular/router';
import { Subscription, take, filter } from 'rxjs';
import { APIResponse } from 'fakturnia-shared';
import { ApiService } from 'shared/services/api.service';
import { Utils } from 'fakturnia-shared';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateTime } from 'luxon';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterOutlet,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatTooltipModule
  ],
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss']
})
export class UserComponent implements OnInit, OnDestroy {

  private _subscriptions: Subscription[] = []
  user: any = null
  isEditable = false
  isLoading = true
  selectedTab: string = 'informacje'
  userId: string = ''
  
  tabs = [
    { id: 'informacje', label: 'Informacje', icon: 'tabler_outline:user', route: 'info' },
    { id: 'dokumenty', label: 'Dokumenty', icon: 'tabler_outline:file-invoice', route: 'documents' },
    { id: 'produkty', label: 'Produkty', icon: 'tabler_outline:package', route: 'products' },
    { id: 'klienci', label: 'Klienci', icon: 'tabler_outline:users', route: 'clients' },
    { id: 'urzadzenia', label: 'Urządzenia', icon: 'tabler_outline:devices', route: 'devices' },
    { id: 'ustawienia', label: 'Ustawienia', icon: 'tabler_outline:settings', route: 'settings' },
    { id: 'subskrypcja', label: 'Subskrypcja', icon: 'tabler_outline:credit-card', route: 'subscription' },
    { id: 'narzedzia', label: 'Narzędzia', icon: 'tabler_outline:tool', route: 'tools' }
  ]

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _apiService: ApiService
  ) {}

  ngOnInit(): void {
    // Pobierz ID użytkownika z parametrów
    this._subscriptions.push(
      this._route.params.subscribe((params: any) => {
        if (params?.id && !Utils.isNullOrEmpty(params.id)) {
          this.userId = params.id;
          this._getUser(params.id);
        }
      })
    );

    // Nasłuchuj zmian routingu, aby zaktualizować selectedTab
    this._subscriptions.push(
      this._router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        this._updateSelectedTab();
      })
    );

    // Ustaw początkowy tab
    this._updateSelectedTab();
  }

  private _updateSelectedTab(): void {
    // Znajdź aktywny tab na podstawie aktualnego URL
    const url = this._router.url;
    const activeTab = this.tabs.find(tab => url.includes(`/${tab.route}`));
    if (activeTab) {
      this.selectedTab = activeTab.id;
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe())
  }

  private _getUser(userId) {
    this.isLoading = true
    this._apiService.getUser(userId)
      .pipe(take(1))
      .subscribe({
        next: (response: APIResponse) => {
          this.user = response.data
          this.isLoading = false
        },
        error: () => {
          this.isLoading = false
        }
      })
  }

  public toggleEditMode(): void {
    this.isEditable = !this.isEditable
  }

  public getInitials(): string {
    if (!this.user) return ''
    const first = this.user.firstName?.charAt(0) || ''
    const last = this.user.lastName?.charAt(0) || ''
    return (first + last).toUpperCase()
  }

  public formatDate(date: any): string {
    if (!date) return '-'
    return DateTime.fromISO(date).toFormat('dd.MM.yyyy HH:mm')
  }

  public getAccountAge(): string {
    if (!this.user?.createdAt) return '-'
    
    const created = DateTime.fromISO(this.user.createdAt)
    const now = DateTime.now()
    const diff = now.diff(created, ['years', 'months', 'days', 'hours'])
    
    if (diff.years >= 1) {
      const months = Math.floor(diff.months)
      return `${Math.floor(diff.years)} ${this.pluralize(Math.floor(diff.years), 'rok', 'lata', 'lat')}${months > 0 ? ` i ${months} ${this.pluralize(months, 'miesiąc', 'miesiące', 'miesięcy')}` : ''}`
    } else if (diff.months >= 1) {
      const days = Math.floor(diff.days)
      return `${Math.floor(diff.months)} ${this.pluralize(Math.floor(diff.months), 'miesiąc', 'miesiące', 'miesięcy')}${days > 0 ? ` i ${days} ${this.pluralize(days, 'dzień', 'dni', 'dni')}` : ''}`
    } else if (diff.days >= 1) {
      return `${Math.floor(diff.days)} ${this.pluralize(Math.floor(diff.days), 'dzień', 'dni', 'dni')}`
    } else {
      return `${Math.floor(diff.hours)} ${this.pluralize(Math.floor(diff.hours), 'godzina', 'godziny', 'godzin')}`
    }
  }

  private pluralize(count: number, one: string, few: string, many: string): string {
    if (count === 1) return one
    const mod10 = count % 10
    const mod100 = count % 100
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
    return many
  }

  public getRelativeTime(date: any): string {
    if (!date) return '-'
    const dateTime = DateTime.fromISO(date)
    const now = DateTime.now()
    const diff = now.diff(dateTime, ['days', 'hours', 'minutes'])
    
    if (diff.days >= 1) {
      return `${Math.floor(diff.days)} ${this.pluralize(Math.floor(diff.days), 'dzień', 'dni', 'dni')} temu`
    } else if (diff.hours >= 1) {
      return `${Math.floor(diff.hours)} ${this.pluralize(Math.floor(diff.hours), 'godzina', 'godziny', 'godzin')} temu`
    } else if (diff.minutes >= 1) {
      return `${Math.floor(diff.minutes)} ${this.pluralize(Math.floor(diff.minutes), 'minuta', 'minuty', 'minut')} temu`
    } else {
      return 'przed chwilą'
    }
  }
}
